
"use client";
import { createContext, useContext, useEffect, useState } from "react";
import Loading from "../components/Elements/Loading";
import { iQueryParams } from "../models/interfaceQueryParams";
import { TypeLayout } from "../constants/enums";
import ErrorAgentNotFound from "../components/Error/ErrorAgentNotFound";

type ConfigContextType = {
  baseUrl: string;
  queryParams: iQueryParams;
  embedData: any;
  isReady: boolean;
  isEmbedded: boolean;
  path: string;
  params: Record<string, string>;
  segments: string[];
};

const defaultParams: iQueryParams = {
  incognito: false,
  key_p: "",
  key_u: "",
  type: TypeLayout.Personal,
};

const ConfigContext = createContext<ConfigContextType>({
  baseUrl: "",
  queryParams: defaultParams,
  embedData: null,
  isReady: false,
  isEmbedded: false,
  path: "",
  params: {},
  segments: [],
});

function isInIframe(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

export let globalEmbedData: any = null;
export let globalIsEmbedded: boolean = false;

export const ConfigProvider = ({ children }: { children: React.ReactNode }) => {
  const [baseUrl, setBaseUrl] = useState("");
  const [queryParams, setQueryParams] = useState<iQueryParams>(defaultParams);
  const [embedData, setEmbedData] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [isTypeValid, setIsTypeValid] = useState(true);
  const [isEmbedded, setIsEmbedded] = useState(false);
  const [path, setPath] = useState("");
  const [params, setParams] = useState<Record<string, string>>({});
  const [segments, setSegments] = useState<string[]>([]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const embedded = isInIframe();
    setIsEmbedded(embedded);
    globalIsEmbedded = embedded;

    const pathname = window.location.pathname;
    setPath(pathname);
    const parts = pathname.split("/").filter(Boolean);
    setSegments(parts);
    const paramsObj: Record<string, string> = {};
    parts.forEach((p, i) => {
      paramsObj[`p${i}`] = p;
    });
    setParams(paramsObj);

    if (
      window.location.origin === "http://localhost:3000" ||
      window.location.origin === "https://playdev3-inca.web.app"
    ) {
      setBaseUrl("https://playdev2-inca.web.app");
    } else {
      setBaseUrl(window.location.origin);
    }

    const searchParams = new URLSearchParams(window.location.search);
    const paramsQuery: iQueryParams = {
      incognito: searchParams.get("incognito") === "true",
      key_p: searchParams.get("key_p") || "",
      key_u: searchParams.get("key_u") || "",
      type: (searchParams.get("type") as any) || "conversation",
    };

    const validTypes = Object.values(TypeLayout);
    const isValidType = validTypes.includes(paramsQuery.type as TypeLayout);
    setIsTypeValid(isValidType);
    setQueryParams(paramsQuery);

    let messageHandler: ((event: MessageEvent) => void) | null = null;
    let readyTimer: NodeJS.Timeout | null = null;
    
    if (embedded) {
      messageHandler = (event: MessageEvent) => {
        if (event.data?.type === "CHATBOT_CLIENT_EMBEDDED") {
          setEmbedData(event.data.payload);
          globalEmbedData = event.data.payload;
          setIsReady(true);
        }
      }; 
      window.addEventListener("message", messageHandler);
      const notifyReady = () => {
        window.parent.postMessage(
          {
            type: "CHATBOT_IFRAME_READY",
          },
          "*" // Cambiar por dominio específico
        );
      };

      notifyReady();
      readyTimer = setTimeout(() => {
        setIsReady(true);
      }, 2000); // segundos de espera

    } else {      
      globalEmbedData = null;
      globalIsEmbedded = false;
      setIsReady(true);
    }
    return () => {
      if (messageHandler) {
        window.removeEventListener("message", messageHandler);
      }
      if (readyTimer) {
        clearTimeout(readyTimer);
      }
    };
  }, []);

  // Loading screen
  if (!isReady) {
    return (
      <div className="w-full h-screen flex flex-1 flex-col items-center justify-center">
        <Loading w="w-3" h="h-3" className="ml-2" />
        <p className="mt-4 text-sm text-gray-500">
          {isEmbedded ? "Esperando configuración..." : "Inicializando..."}
        </p>
      </div>
    );
  }

  // Error por tipo inválido
  if (!isTypeValid) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ErrorAgentNotFound
          message="El tipo de agente indicado no es válido o no existe."
          onReturn={() => window.location.assign(window.location.origin)}
        />
      </div>
    );
  }

  return (
    <ConfigContext.Provider
      value={{
        baseUrl,
        queryParams,
        embedData,
        isReady,
        isEmbedded,
        path,
        params,
        segments,
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfig = () => useContext(ConfigContext);